import type { ContactMatchStatus, Submission } from "@/api/submissions.service";
import { ROUTES } from "@/routes";
import { withQueryParam } from "@/utils/returnTo";

/** Permission for the PATCH contact / rematch endpoints (submission-api routes.ts). */
export const SUBMISSION_WRITE_PERMISSION = "staff.submission.write";

export const SUBMISSION_READ_PERMISSION = "staff.submission.read";

/**
 * Staff-scope flag that gates the whole contact-link UI on the submissions list (Lawyer
 * column, Contact match filter, Link/Change contact, modal reopen, CSV contact columns).
 * The same flag gates the matcher in submission-api.
 */
export const CONTACT_MATCH_FEATURE_FLAG = "submission-entry-contact-match";

/** The modal searches the Address Book directly, so linking also needs read access there. */
export const ADDRESS_BOOK_READ_PERMISSION = "addressbook.read";

/**
 * Query params of the create-contact round trip: the list puts `linkContactEntry` in the
 * returnTo it hands ContactDetail, ContactDetail appends `createdContactId` after saving.
 */
export const LINK_CONTACT_ENTRY_PARAM = "linkContactEntry";
export const CREATED_CONTACT_ID_PARAM = "createdContactId";

/** Contact linking only exists for lawyer-recommended submissions (spec §3.1). */
export function isLawyerSubmission(
  row: Pick<Submission, "submissionType" | "refereeType">,
): boolean {
  return row.submissionType === "lawyer" && row.refereeType === "lawyer";
}

/**
 * The researcher-facing state of a lawyer entry's link. `manual` splits in two: staff
 * either linked a contact or confirmed there is none.
 */
export type ContactLinkState = "linked" | "auto" | "needs_review" | "pending" | "no_contact";

export function getContactLinkState(
  row: Pick<Submission, "contactMatchStatus" | "contactId">,
): ContactLinkState | null {
  switch (row.contactMatchStatus) {
    case "manual":
      return row.contactId ? "linked" : "no_contact";
    case "auto_matched":
      return "auto";
    case "ambiguous":
    case "unmatched":
      return "needs_review";
    case "pending":
      return "pending";
    default:
      return null;
  }
}

export const CONTACT_LINK_STATE_LABELS: Record<ContactLinkState, string> = {
  linked: "Linked",
  auto: "Auto",
  needs_review: "Needs review",
  pending: "Pending",
  no_contact: "No contact",
};

/** Tooltip text: what the pill means and, where useful, what to do about it. */
export const CONTACT_LINK_STATE_DESCRIPTIONS: Record<ContactLinkState, string> = {
  linked: "Linked to an Address Book contact by staff",
  auto: "Linked automatically: name match corroborated by country or ranking",
  needs_review: "No confident match: pick a contact or confirm there is none",
  pending: "Auto-match not attempted yet",
  no_contact: "Staff confirmed there is no matching Address Book contact",
};

/**
 * The list's Contact match filter. "Set by staff" covers both a manual link and a
 * confirmed "no contact": submission-api filters by status only, and both are `manual`.
 */
export type ContactMatchFilterValue =
  | "all"
  | "needs_review"
  | "auto_matched"
  | "manual"
  | "pending";

export const CONTACT_MATCH_FILTER_STATUSES: Record<
  Exclude<ContactMatchFilterValue, "all">,
  ContactMatchStatus[]
> = {
  needs_review: ["ambiguous", "unmatched"],
  auto_matched: ["auto_matched"],
  manual: ["manual"],
  pending: ["pending"],
};

export const CONTACT_MATCH_FILTER_OPTIONS: { value: ContactMatchFilterValue; label: string }[] = [
  { value: "all", label: "All" },
  { value: "needs_review", label: "Needs review" },
  { value: "auto_matched", label: "Auto-matched" },
  { value: "manual", label: "Set by staff (linked or no contact)" },
  { value: "pending", label: "Pending" },
];

export function contactMatchStatusesFor(value: string): ContactMatchStatus[] | undefined {
  if (value in CONTACT_MATCH_FILTER_STATUSES) {
    return CONTACT_MATCH_FILTER_STATUSES[value as keyof typeof CONTACT_MATCH_FILTER_STATUSES];
  }
  return undefined;
}

/**
 * Where "Create new contact" goes: the office route when we know the office (it
 * preselects company and office), else the company route. `name` prefills first/last
 * name; `returnTo` brings the operator back with the new contact's id.
 */
export function buildCreateContactUrl(options: {
  officeId: string | null;
  companyId: string;
  personName: string | null | undefined;
  returnTo: string | null;
}): string {
  let path = options.officeId
    ? ROUTES.addressBook.officeContactNew(options.officeId)
    : ROUTES.addressBook.companyContactNew(options.companyId);
  if (options.personName) path = withQueryParam(path, "name", options.personName);
  if (options.returnTo) path = withQueryParam(path, "returnTo", options.returnTo);
  return path;
}

/** Case-insensitive, whitespace-tolerant country comparison (as submission-api's findMatchingOffice). */
export function sameCountry(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
